import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import FounderAvatar from "@/components/site/FounderAvatar";

function ColegiadoLightbox({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      data-testid="colegiado-lightbox"
      className="fixed inset-0 z-50 bg-[#04120E]/85 backdrop-blur-sm flex items-center justify-center p-4 lg:p-10"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between mb-4 text-white">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A84C]">
              Col·legi Oficial de Psicologia de Catalunya
            </div>
            <div className="font-display text-lg md:text-xl truncate">Carnet de colegiado — Francesc Riera</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="colegiado-lightbox-close"
            className="w-11 h-11 rounded-full border border-white/25 grid place-items-center text-white hover:bg-[#C9A84C] hover:text-[#06231D] hover:border-[#C9A84C] transition-colors flex-shrink-0"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        <div className="flex-1 min-h-0 min-w-0 rounded-xl bg-white shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden flex items-center justify-center p-2">
          <div className="relative min-w-0 min-h-0" style={{ width: "fit-content", height: "fit-content", maxWidth: "100%", maxHeight: "100%" }}>
            <img
              src="/carnet-copc.png"
              alt="Carnet de psicólogo colegiado de Francesc Riera, Col·legi Oficial de Psicologia de Catalunya"
              className="block w-auto h-auto select-none"
              style={{ maxWidth: "100%", maxHeight: "calc(92vh - 170px)" }}
              draggable={false}
            />
            {/* DNI/NIF difuminado por privacidad */}
            <span
              aria-hidden
              className="absolute backdrop-blur-md bg-white/10 rounded-sm"
              style={{ top: "49.8%", left: "71.5%", width: "26%", height: "6.5%" }}
            />
          </div>
        </div>

        <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-white/50 text-center">
          Datos personales difuminados por privacidad · documento oficial disponible a petición
        </p>
      </motion.div>
    </motion.div>
  );
}

export default function AboutFounder() {
  const [carnetOpen, setCarnetOpen] = useState(false);

  return (
    <section
      id="sobre-mi"
      data-testid="about-section"
      className="relative bg-[#F7F9F8] text-[#06231D] py-28 lg:py-40 grain overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 lg:items-start mb-16 lg:mb-24">
          <Reveal className="lg:col-span-4 lg:self-start">
            <div className="flex items-center gap-4">
              <span className="font-serif-italic text-[#085041] text-[9rem] lg:text-[12rem] leading-none -mt-5 lg:-mt-8 block">06</span>
              <span className="inline-block text-xs lg:text-sm uppercase tracking-[0.18em] font-medium text-[#F7F9F8] bg-[#06231D] px-3 py-2 rounded-md">Sobre mí</span>
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-[22ch]">
                Detrás de OptimizaRRHH está <span className="font-serif-italic font-normal text-[#085041]">Francesc Riera</span>.
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-stretch">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative w-full max-w-[520px]">
              <FounderAvatar />

              {/* Sello de colegiado — abre el carnet */}
              <button
                type="button"
                onClick={() => setCarnetOpen(true)}
                data-testid="colegiado-seal-button"
                aria-label="Ver carnet de psicólogo colegiado"
                className="absolute -bottom-5 -right-3 lg:-right-6 z-10 w-28 h-28 lg:w-[150px] lg:h-[150px] rounded-full bg-[#06231D] border-2 border-[#C9A84C] -rotate-[8deg] flex flex-col items-center justify-center text-center text-[#C9A84C] shadow-[0_18px_40px_-14px_rgba(6,35,29,0.55)] transition-all duration-500 hover:bg-[#0B5540] hover:border-[#2EC48A] hover:shadow-[0_0_44px_rgba(46,196,138,0.45)] hover:scale-105 cursor-pointer"
              >
                <span className="text-[7px] lg:text-[9px] uppercase tracking-[0.14em] lg:tracking-[0.22em] font-semibold">Psicólogo</span>
                <span className="font-serif-italic text-base lg:text-[22px] text-[#F7F9F8] leading-none my-1">nº 34905</span>
                <span className="text-[7px] lg:text-[9px] uppercase tracking-[0.12em] lg:tracking-[0.22em] font-semibold">Colegiado · COPC</span>
              </button>
            </div>

            {/* Firma bajo la fotografía */}
            <div className="mt-8 flex flex-col gap-1">
              <span className="font-display text-2xl text-[#06231D]">Francesc Riera</span>
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#085041]/70">Psicólogo · Consultor de RRHH para startups y tech</span>
            </div>
          </motion.div>

          <div className="lg:col-span-7 flex flex-col gap-5 justify-between text-[#06231D]/80 text-base lg:text-[17px] leading-relaxed font-light max-w-2xl text-justify hyphens-auto h-full">
            <Reveal>
              <p>
                Soy Francesc, <span className="font-medium text-[#06231D]">psicólogo organizacional</span>, y trabajo desde la isla de Mallorca con <span className="font-medium text-[#06231D]">fundadores de startups y empresas tecnológicas</span> de España y Latinoamérica.
              </p>
            </Reveal>
            <Reveal delay={0.06}>
              <p>
                Siempre me he considerado alguien muy curioso. Y lo que me trajo hasta aquí fue la curiosidad: me asombra observar cómo los cambios tecnológicos que estamos viviendo se cruzan con la forma en que las personas piensan, se motivan y trabajan juntas.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <p>
                OptimizaRRHH surgió de mi inquietud sobre cómo usar esos cambios para <span className="font-medium text-[#06231D]">dar luz y claridad</span> a algo que muchas veces se gestiona a ojo y de malas maneras: el talento. Mi misión es ayudar a crear <span className="font-medium text-[#06231D]">organizaciones más sanas y felices</span>, capaces de aportar lo mejor de sí mismas al mundo.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p>
                En varias empresas por las que pasé vi cómo <span className="font-medium text-[#06231D]">se descuidaba al talento</span> y cómo eso, tarde o temprano, acababa causando problemas, tanto para los empleados como para la empresa. Y en una startup, donde <span className="font-medium text-[#06231D]">cada persona pesa muchísimo</span>, eso se nota todavía más.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <p>
                Por eso estudié <span className="font-medium text-[#06231D]">psicología social y organizacional</span> en la Universitat de Barcelona (UB), me formé en <span className="font-medium text-[#06231D]">gestión del talento y competencias digitales</span> en la Universitat Internacional de Valencia (VIU) y aprendí sobre <span className="font-medium text-[#06231D]">coaching y preparación mental</span> en Quantum BCN. Antes de dedicarme a esto trabajé en ventas y administración, y eso me enseñó cómo se vive una empresa desde dentro y qué pasa cuando nadie dedica tiempo (o le dedica muy poco) a preocuparse por las personas.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p>
                Combino lo que sé de psicología con la tecnología para convertir tus dudas sobre cómo gestionar las personas de tu startup en <span className="font-medium text-[#06231D]">un informe claro, con prioridades y herramientas listas para usar</span>. Todo de un modo <span className="font-medium text-[#06231D]">asíncrono</span>: sin reuniones de por medio ni documentos interminables que nadie lee y se quedan olvidados en un cajón o en una carpeta del ordenador. Si algo de cómo gestionas a tu equipo no termina de funcionar, seguramente tenga arreglo, y por algún sitio hay que empezar. <span className="font-medium text-[#06231D]">Yo te ayudo a ver por dónde.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {carnetOpen && <ColegiadoLightbox onClose={() => setCarnetOpen(false)} />}
      </AnimatePresence>
    </section>
  );
}
